import type { Status } from "@amzn/d16-g-client-deck-builder-typescript-client";
import {
    InsightType,
    type Step,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import React, { useMemo } from "react";
import { isNonNullish } from "remeda";

import { useContentCardShown } from "../../../../../hooks/misc/useContentCardShown";
import type { CardDetail } from "../../../../../types/card";
import type { PackagedInsightAiMetadataAttributes } from "../../../../../types/packagedInsight";
import { isSuccess } from "../../../../../utils/generatedInsights.utils";
import { getCardProgress } from "../../../../../utils/insightCard.utils";
import { getAdvertiserId } from "../../../../../utils/permission.utils";
import { CardLayout } from "../../insightDetails/CardLayout";
import type { GeneratedInsightCardProps } from "../GeneratedInsightCard";

import { PackagedInsightCardAction } from "./PackagedInsightCardAction";

export const PackagedInsightCard: React.FunctionComponent<
    GeneratedInsightCardProps
> = ({ insight }) => {
    const {
        identifier: executionId,
        browseNode,
        brand,
        title,
        template,
        step,
        status,
        metadata,
        aiEnhancedStatus,
    } = insight;

    const advertiserId = getAdvertiserId();
    const description = isNonNullish(metadata)
        ? metadata["description"]
        : undefined;

    const details: CardDetail[] = useMemo(() => {
        return [
            {
                label: "Brand",
                value: brand,
            },
            {
                label: "BN",
                value: browseNode,
                lineClamp: 2,
            },
            {
                label: "Template",
                value: template,
            },
        ];
    }, [brand, browseNode, template]);

    useContentCardShown({
        insightType: InsightType.IHUB,
        identifier: executionId,
    });

    return (
        <CardLayout
            key={executionId}
            dataTestId={`pkg-${executionId}`}
            details={details}
            description={title ?? ""}
            actions={
                <PackagedInsightCardAction
                    title={title!}
                    details={details}
                    aiEnhancedStatus={aiEnhancedStatus}
                    executionId={executionId!}
                    advertiserId={advertiserId!}
                    progress={getCardProgress(step as Step, status as Status)}
                    description={description}
                    insight={insight}
                    aiMetadata={
                        metadata as Record<
                            PackagedInsightAiMetadataAttributes,
                            string
                        >
                    }
                />
            }
            tag={isSuccess(aiEnhancedStatus) ? "AI Enhanced" : undefined}
        />
    );
};
